import { Controller } from "@hotwired/stimulus"
import { applyFloatingPanel, placeFloatingPanel } from "../lib/floating_panel"

// A filterable select, in single or multi mode.
//
//   data-controller="combobox"
//   data-combobox-mode-value="single" | "multi"
//   data-combobox-search-threshold-value="4"   ← below this, no search field
//
// Single mode writes one hidden input and closes on pick. Multi mode leaves the
// panel open and lets the host controller own the tokens — it just announces
// the pick via a `combobox:select` event, because the two multi-selects in this
// app (car classes, tracks) already have their own token markup and hidden
// inputs that must not be re-implemented here.
//
// The panel is `position: fixed`, which already escapes an ancestor's
// `overflow-hidden` — a fixed element is only clipped by an ancestor that
// establishes a containing block (transform/filter/contain), and none of the
// cards here do. It is deliberately NOT re-parented to <body>: moving it takes
// it out of this controller's scope, and Stimulus then unbinds every action
// inside it, so the options stop responding to clicks.
//
// It flips above the trigger when there isn't room below, caps its own height
// to the space it has, and clamps to the viewport — all of which lives in
// lib/floating_panel so the track picker cannot place itself differently.
//
// Opening one menu closes every other. It can't ride on the document click
// handler: `toggle` stops propagation (otherwise the very click that opens a
// panel would reach the handler and close it again), so the other instances
// never see it.
const OPENED = "combobox:opened"

export default class extends Controller {
  static targets = [
    "trigger",
    "panel",
    "search",
    "option",
    "count",
    "label",
    "scroller",
  ]
  static values = {
    mode: { type: String, default: "single" },
    searchThreshold: { type: Number, default: 4 },
    open: { type: Boolean, default: false },
  }

  connect() {
    this._onDocumentClick = (e) => {
      if (!this.openValue) return
      if (this.element.contains(e.target)) return
      if (this.panel && this.panel.contains(e.target)) return
      this.close()
    }
    this._reposition = () => {
      if (this.openValue) this._place()
    }
    this._onOtherOpened = (e) => {
      if (e.detail?.source !== this) this.close()
    }

    document.addEventListener("click", this._onDocumentClick)
    document.addEventListener(OPENED, this._onOtherOpened)
    window.addEventListener("resize", this._reposition)
    // `true` = capture: a scroll inside any ancestor must move the panel too,
    // and scroll doesn't bubble.
    window.addEventListener("scroll", this._reposition, true)

    // Held directly: once the panel is re-parented to <body> it leaves this
    // controller's scope, and `this.panelTarget` stops resolving — which threw
    // right after the detach and left the panel unpositioned at 0,0.
    this.panel = this.panelTarget
    this.search = this.hasSearchTarget ? this.searchTarget : null
    this.options = this.optionTargets
    this.count = this.hasCountTarget ? this.countTarget : null
    this.scroller = this.hasScrollerTarget ? this.scrollerTarget : null

    // Arrow-key navigation moves a visual highlight, not focus (focus stays in
    // the filter field). Without ids to point `aria-activedescendant` at, that
    // movement is silent to a screen reader.
    // The listbox id is the per-instance unique one (the wrapper has none), so
    // option ids derived from it can't collide with another combobox on the page.
    this.list = this.panel.querySelector("[role='listbox']")
    this.options.forEach((o, i) => {
      if (!o.id) o.id = `${this.list?.id || "combobox"}-opt-${i}`
    })
    if (this.search && this.list)
      this.search.setAttribute("aria-controls", this.list.id)

    this._applySearchVisibility()
    this._syncCount()
  }

  disconnect() {
    document.removeEventListener("click", this._onDocumentClick)
    document.removeEventListener(OPENED, this._onOtherOpened)
    window.removeEventListener("resize", this._reposition)
    window.removeEventListener("scroll", this._reposition, true)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.openValue ? this.close() : this.open()
  }

  open() {
    if (this.openValue) return
    document.dispatchEvent(
      new CustomEvent(OPENED, { detail: { source: this } }),
    )
    this.openValue = true
    this.panel.classList.remove("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "true")
    // No cursor on open — only the current value is tinted, and the first arrow
    // press moves relative to it.
    this._clearCursor()

    this._place()

    if (this._searchVisible) {
      this.search.value = ""
      this._filter("")
      requestAnimationFrame(() => this.search.focus())
    }
    this._scrollSelectedIntoView()
  }

  close() {
    if (!this.openValue) return
    this.openValue = false
    this.panel.classList.add("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this._clearCursor()
  }

  // Typing clears the cursor: the row it was on may not even be in the list any
  // more, and a cursor left behind on a filtered list is a promise about what
  // Enter does that the list no longer keeps.
  filter() {
    this._clearCursor()
    this._filter(this.search.value)
  }

  // The host gated options in or out; re-run the filter so the list and its
  // count catch up.
  refresh() {
    this._filter(this._searchVisible ? this.search.value : "")
  }

  select(event) {
    const option = event.currentTarget
    if (option.hasAttribute("aria-disabled")) return

    const detail = {
      value: option.dataset.comboboxOptionValue,
      label: option.dataset.comboboxOptionLabel || option.textContent.trim(),
    }

    if (this.modeValue === "multi") {
      // The host owns the tokens and the hidden inputs; we only announce.
      this.dispatch("select", { detail })
      if (this._searchVisible) {
        this.search.value = ""
        this._filter("")
        this.search.focus()
      }
      return
    }

    this._selectSingle(option, detail)
    this.dispatch("select", { detail })
    this.close()
    this.triggerTarget.focus()
  }

  keydown(event) {
    if (event.key === "Escape") {
      if (!this.openValue) return
      event.preventDefault()
      this.close()
      this.triggerTarget.focus()
      return
    }

    if (!this.openValue) {
      if (["ArrowDown", "Enter", " "].includes(event.key)) {
        event.preventDefault()
        this.open()
      }
      return
    }

    const visible = this._visibleOptions()
    if (visible.length === 0) return

    // On open there is no cursor — only the current value is tinted. The first
    // arrow press therefore has to start *from that value*, not from row 0:
    // pressing ↓ in a list whose 9th option is selected should offer the 10th,
    // not walk back up from the top.
    const cursor = visible.findIndex((o) => o.dataset.active === "true")
    const from =
      cursor >= 0
        ? cursor
        : visible.findIndex((o) => o.getAttribute("aria-selected") === "true")

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const step = event.key === "ArrowDown" ? 1 : -1
      // Wraps at both ends: a 5-option list is faster to reach round the back.
      const next =
        from < 0
          ? step > 0
            ? 0
            : visible.length - 1
          : (from + step + visible.length) % visible.length
      this._activate(visible, next)
    } else if (event.key === "Enter") {
      event.preventDefault()
      // No cursor means "take the top match" — the filter is what narrowed it.
      const target = cursor >= 0 ? visible[cursor] : visible[0]
      target?.click()
    }
  }

  // ───── internals ─────

  _selectSingle(option, detail) {
    this.options.forEach((o) => {
      const chosen = o === option
      o.setAttribute("aria-selected", String(chosen))
      o.querySelector("[data-combobox-check]")?.classList.toggle(
        "hidden",
        !chosen,
      )
    })
    // Writing a hidden input's value fires nothing on its own, so a host bound
    // to `change` on it never heard the pick: the setup wizard's race duration
    // didn't re-render its schedule preview, and the planner's autosaving
    // fields didn't save. `change` is the contract for "this input's value was
    // committed by the user", and it's what track-picker already dispatches —
    // multi mode still announces via `combobox:select`, since there the host
    // owns the inputs.
    const input = this.element.querySelector("[data-combobox-input]")
    if (input) {
      input.value = detail.value
      input.dispatchEvent(new Event("change", { bubbles: true }))
    }
    // Rebuilt rather than copied: the list stacks label over sublabel, the
    // trigger sets them side by side, so the two faces aren't interchangeable.
    if (this.hasLabelTarget) this._renderTriggerFace(option, detail.label)
  }

  _renderTriggerFace(option, label) {
    const dot = option.dataset.comboboxOptionDot
    const sub = option.dataset.comboboxOptionSublabel
    const parts = []
    if (dot) {
      parts.push(
        `<span class="h-2 w-2 shrink-0 rounded-full" style="background: ${dot};" aria-hidden="true"></span>`,
      )
    }
    const subHtml = sub
      ? `<span class="font-mono truncate text-[10.5px] text-app-text-faint">${sub}</span>`
      : ""
    parts.push(
      `<span class="flex min-w-0 flex-1 items-baseline gap-2">` +
        `<span class="truncate text-app-text">${label}</span>${subHtml}</span>`,
    )
    this.labelTarget.innerHTML = parts.join("")
  }

  _filter(term) {
    const q = term.trim().toLowerCase()
    let shown = 0
    this.options.forEach((o) => {
      const hay = (
        o.dataset.comboboxOptionSearch || o.textContent
      ).toLowerCase()
      // `gameOk=false` is the host controller gating an option out (a car
      // class that doesn't belong to the chosen game, or one already picked).
      // Kept separate from the text filter so the two don't fight over
      // `hidden`.
      const eligible = o.dataset.gameOk !== "false"
      const match = eligible && (q === "" || hay.includes(q))
      o.classList.toggle("hidden", !match)
      if (match) shown += 1
    })
    // Group headings vanish when nothing under them survived the filter.
    this.panel.querySelectorAll("[data-combobox-group]").forEach((group) => {
      const any = [
        ...group.querySelectorAll("[data-combobox-target='option']"),
      ].some((o) => !o.classList.contains("hidden"))
      group.classList.toggle("hidden", !any)
    })
    const empty = this.panel.querySelector("[data-combobox-empty]")
    if (empty) empty.classList.toggle("hidden", shown > 0)
    this._syncCount(shown)
  }

  _syncCount(shown) {
    if (!this.count) return
    const total = this.options.length
    this.count.textContent = `${shown ?? total} of ${total}`
  }

  _visibleOptions() {
    return this.options.filter(
      (o) =>
        !o.classList.contains("hidden") && !o.hasAttribute("aria-disabled"),
    )
  }

  _clearCursor() {
    this.options.forEach((o) => delete o.dataset.active)
    this.search?.removeAttribute("aria-activedescendant")
  }

  _activate(visible, index) {
    this._clearCursor()
    const option = visible[index]
    if (!option) return
    option.dataset.active = "true"
    this.search?.setAttribute("aria-activedescendant", option.id)
    this._reveal(option)
  }

  _scrollSelectedIntoView() {
    const selected = this.options.find(
      (o) => o.getAttribute("aria-selected") === "true",
    )
    if (selected) this._reveal(selected)
  }

  // Scrolls the panel's own list, never `scrollIntoView` — that walks up every
  // scrollable ancestor and moves the page (and the drawer's round list) out
  // from under the trigger the panel is anchored to.
  _reveal(option) {
    const list = this.scroller
    if (!list) return
    const top = option.offsetTop - list.offsetTop
    const bottom = top + option.offsetHeight
    if (top < list.scrollTop) list.scrollTop = top
    else if (bottom > list.scrollTop + list.clientHeight)
      list.scrollTop = bottom - list.clientHeight
  }

  get _searchVisible() {
    if (!this.search) return false
    return !this.search
      .closest("[data-combobox-search-row]")
      ?.classList.contains("hidden")
  }

  // Under the threshold a search field is furniture: the list is already short
  // enough to read in one glance.
  _applySearchVisibility() {
    const row = this.panel.querySelector("[data-combobox-search-row]")
    if (!row) return
    row.classList.toggle(
      "hidden",
      this.options.length < this.searchThresholdValue,
    )
  }

  _place() {
    applyFloatingPanel(this.panel, placeFloatingPanel(this.triggerTarget))
  }
}
